import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, EyeOff, ShieldAlert } from 'lucide-react'
import type { ReactNode } from 'react'

import { branchListQueryOptions } from '@/api/reference-api'
import type { Branch, CustomerDetail } from '@/api/types'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Skeleton } from '@/components/ui/skeleton'
import { CustomerEditForm } from '@/features/customers/edit/customer-edit-form'
import { CUSTOMER_PERMISSIONS, useCustomerPermissions } from '@/features/customers/permissions'
import { useCustomer } from '@/features/customers/queries'

/**
 * Amending a customer file.
 *
 * Two permissions, not one. `customers.update` is the obvious half. The second, `customers.pii.view`,
 * is not a policy this screen invents — it is a consequence of how the endpoint works. The update
 * takes the complete record, and without the PII permission the Ghana Card number and phone arrive
 * masked while the date of birth does not arrive at all. A form seeded from those values would
 * write `****789-0` into the customer's identity anchor the moment it was saved. So the screen
 * refuses, and says why, rather than offering an edit that silently destroys the record.
 *
 * A file in `PendingReview` is left alone as well, though for a different reason: it has been
 * handed to a supervisor, and letting the branch keep editing underneath a review is how an
 * approval ends up given to a record nobody read.
 */
export function CustomerEditPage() {
  const { customerId } = useParams({ from: '/authenticated/customers/$customerId/edit' })
  const navigate = useNavigate()
  const permissions = useCustomerPermissions()

  const query = useCustomer(customerId)
  const branches = useQuery(branchListQueryOptions())

  const backToCustomer = (
    <Link
      to="/customers/$customerId"
      params={{ customerId }}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover max-lg:min-h-11 max-lg:py-2"
    >
      <ArrowLeft className="size-3.5" aria-hidden />
      Back to the customer
    </Link>
  )

  if (query.isPending) {
    return (
      <>
        {backToCustomer}
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-96 w-full" />
        </div>
      </>
    )
  }

  if (query.isError) {
    return (
      <>
        {backToCustomer}
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="This customer could not be opened"
        />
      </>
    )
  }

  const customer = query.data

  return (
    <>
      {backToCustomer}

      <PageHeader
        title={`Amend ${customer.fullName}`}
        reference={customer.customerNumber}
        badges={
          <>
            <StatusBadge status={customer.status} />
            <StatusBadge status={customer.kycStatus} />
          </>
        }
        description="The whole file is saved, so every value has to be present — including the ones this amendment does not touch."
      />

      {!permissions.canUpdate ? (
        <Refusal
          icon={<ShieldAlert className="size-6 text-warning-text" aria-hidden />}
          title="Amending a customer is restricted"
        >
          It needs the <Code>{CUSTOMER_PERMISSIONS.update}</Code> permission. Ask a supervisor to make
          the change, or to have the permission added to your role.
        </Refusal>
      ) : !permissions.canViewPii ? (
        <Refusal
          icon={<EyeOff className="size-6 text-warning-text" aria-hidden />}
          title="This file cannot be amended from a masked copy"
        >
          Without <Code>{CUSTOMER_PERMISSIONS.piiView}</Code> the Ghana Card number and phone number
          reach this browser masked, and the date of birth is not sent at all. The update endpoint
          takes the complete record, so saving a form built from masked values would write the mask
          into the customer&apos;s identity anchor. The refusal is the safe answer; ask someone
          holding that permission to make the change.
        </Refusal>
      ) : customer.kycStatus === 'PendingReview' ? (
        <Refusal
          icon={<ShieldAlert className="size-6 text-warning-text" aria-hidden />}
          title="This file is with a supervisor"
        >
          {customer.fullName}&apos;s KYC file is pending review. Editing underneath a review is how
          an approval gets given to a record nobody read — wait for the decision. If it is rejected
          it comes back to the branch for correction, and if it is approved it can be amended again
          from here.
        </Refusal>
      ) : branches.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : branches.isError ? (
        // Not fatal. The branch select needs the list, and every other field on the screen does
        // not, so the amendment goes ahead with the branch the record already carries.
        <CustomerEditForm
          customer={customer}
          branches={fallbackBranches(customer)}
          onSaved={() => {
            void navigate({ to: '/customers/$customerId', params: { customerId } })
          }}
          onCancel={() => {
            void navigate({ to: '/customers/$customerId', params: { customerId } })
          }}
        />
      ) : (
        <CustomerEditForm
          customer={customer}
          branches={branches.data.length > 0 ? branches.data : fallbackBranches(customer)}
          onSaved={() => {
            void navigate({ to: '/customers/$customerId', params: { customerId } })
          }}
          onCancel={() => {
            void navigate({ to: '/customers/$customerId', params: { customerId } })
          }}
        />
      )}
    </>
  )
}

/**
 * The one branch this record is already held at, so the select has the record's own value in it
 * when the branch list cannot be read. Moving a customer between branches then is not offered,
 * which is the right restriction: it is a transfer, not a correction.
 */
const fallbackBranches = (customer: CustomerDetail): Branch[] => [
  {
    id: customer.branchId,
    code: customer.branchId,
    name: customer.branchName || `Branch ${customer.branchId}`,
    isHomeBranch: false,
  },
]

function Code({ children }: { children: ReactNode }) {
  return <span className="font-mono text-xs">{children}</span>
}

function Refusal({
  icon,
  title,
  children,
}: {
  icon: ReactNode
  title: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
      {icon}
      <p className="text-sm font-medium text-heading">{title}</p>
      <p className="max-w-lg text-xs text-muted">{children}</p>
    </div>
  )
}
